import {
  BundleItemType,
  BundleStatus,
  GetCollectionParentOffersDetailedQuery,
  GetCollectionParentOffersQuery,
  useGetCollectionParentOffersDetailedQuery,
} from 'generated/graphql';
import { useState } from 'react';
import { Async } from '@client/new/helpers/types';
import { Collection } from '../collection.domain';
import { ColorsVars } from '@design-system/vars.css';
import { Sprinkles } from '@design-system/sprinkles.css';
import {
  FeatureCodeById,
  toBundleContentDescription,
} from '@client/new/pages/programs/model/programs.mappers';
import { statusColor } from '@client/new/pages/bundles/otp/viewModel/otpBundle.vm.mapper';
import { formatPrice } from '../mappers';
import { capitalize, orderBy } from 'lodash';
import { navigate, PATHS } from '@client/routes';
import { setOfferPrefillFromProgram } from '@client/new/pages/bundles/common/offerPrefill';

type OfferDetails = {
  id: number;
  title: string;
  bgColor: ColorsVars;
  opacity: Sprinkles['opacity'];
};

export type OfferBundleLabel = {
  id: number;
  name: string;
  status: {
    label: string;
    color: {
      bg: ColorsVars;
      text: ColorsVars;
    };
  };
  contentDescription: string;
  price: string;
  isFree: boolean;
  bgColor: ColorsVars;
  opacity: Sprinkles['opacity'];
};

export type UnsavedChangesModal = {
  isOpen: boolean;
  saving: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSave: () => void;
};

type UsePricingModel = {
  offers: OfferBundleLabel[];
  currentPrice: number | null;
  addToOffer: () => void;
  openOffer: (offerId: number) => void;
  unsavedChangesModal: UnsavedChangesModal;
};

type PricingModelParams = {
  creatorId: number;
  collection: Collection;
  /** Whether the About tab has unsaved edits pending. */
  isModified: boolean;
  /** Persists the pending About tab changes. Resolves `true` on success. */
  save: () => Promise<boolean>;
};

export const usePricingModel = ({
  creatorId,
  collection,
  isModified,
  save,
}: PricingModelParams): Async<UsePricingModel> => {
  const { data, loading, error } = useGetCollectionParentOffersDetailedQuery({
    variables: {
      input: {
        itemType: BundleItemType.Program,
        itemId: collection.id,
      },
      creatorId,
    },
  });
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  if (loading) return { type: 'fetching' };
  if (error) return { type: 'fetch-error', value: error.message };

  const price = collection.exclusiveProgramMetadata?.price || null;

  const featureCodeById: FeatureCodeById = new Map(
    (data?.creator?.features ?? [])
      .filter((f): f is NonNullable<typeof f> => Boolean(f))
      .map((f) => [f.id, f.code] as const),
  );

  const redirectToOffer = () => {
    setOfferPrefillFromProgram({
      programId: collection.id,
      name: collection.name ?? '',
      description: collection.description,
      coverImage: collection.mediaCover
        ? {
            image: collection.mediaCover.image ?? '',
            cdn01: collection.mediaCover.cdn01 ?? '',
          }
        : null,
    });
    navigate(PATHS.BUNDLES(creatorId));
  };

  return {
    type: 'fetched',
    value: {
      offers: mapRawOffersToBundleLabels(
        data?.parentBundlesByItem || [],
        featureCodeById,
      ),
      currentPrice: price ?? null,
      openOffer: (offerId: number) =>
        navigate(PATHS.BUNDLES(creatorId, offerId)),
      addToOffer: () => {
        if (isModified) {
          setIsUnsavedModalOpen(true);
          return;
        }
        redirectToOffer();
      },
      unsavedChangesModal: {
        isOpen: isUnsavedModalOpen,
        saving,
        onClose: () => setIsUnsavedModalOpen(false),
        onDiscard: () => {
          setIsUnsavedModalOpen(false);
          redirectToOffer();
        },
        onSave: async () => {
          setSaving(true);
          try {
            const saved = await save();
            if (saved) {
              setIsUnsavedModalOpen(false);
              redirectToOffer();
            }
          } finally {
            setSaving(false);
          }
        },
      },
    },
  };
};

const mapRawOffersToBundleLabels = (
  rawOffers: GetCollectionParentOffersDetailedQuery['parentBundlesByItem'],
  featureCodeById: FeatureCodeById,
): OfferBundleLabel[] => {
  return orderBy(rawOffers, ['status', 'id'], ['desc', 'desc']).map(
    (offer) => ({
      id: offer.id,
      name: offer.title,
      status: {
        label: capitalize(offer.status),
        color: statusColor[offer.status],
      },
      price: formatPrice(offer.oneTimeProduct?.price ?? null),
      isFree: offer.isFree,
      contentDescription: toBundleContentDescription(offer, featureCodeById),
      bgColor:
        offer.status === BundleStatus.Published
          ? 'G-40'
          : offer.status === BundleStatus.Archived
            ? 'ERROR-20'
            : ('D-40' as ColorsVars),
      opacity:
        offer.status === BundleStatus.Archived
          ? '0.5'
          : ('1' as Sprinkles['opacity']),
    }),
  );
};

export const mapRawOffersToOffersDetails = (
  rawOffers: GetCollectionParentOffersQuery['parentBundlesByItem'],
): OfferDetails[] => {
  return orderBy(rawOffers, ['status', 'id'], ['desc', 'desc']).map(
    (offer) => ({
      id: offer.id,
      title: offer.title,
      bgColor:
        offer.status === BundleStatus.Published
          ? 'G-40'
          : offer.status === BundleStatus.Archived
            ? 'ERROR-20'
            : ('D-40' as ColorsVars),
      opacity:
        offer.status === BundleStatus.Archived
          ? '0.5'
          : ('1' as Sprinkles['opacity']),
    }),
  );
};
